import React, { useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_ORDER_ROUTER } from "../../../services/api-config";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

const PM_CASH = 1;
const PM_QR = 2;
const PM_DEBT = 3;
const LABELS: Record<number, string> = { [PM_CASH]: "Tiền mặt", [PM_QR]: "Chuyển khoản", [PM_DEBT]: "Công nợ" };

interface Props {
  orderId: number;
  orderCode?: string;
  /** Hình thức hiện tại của đơn (1 tiền mặt, 2 chuyển khoản, 3 công nợ). */
  paymentMethod: number;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Đổi hình thức thanh toán của đơn ĐÃ HOÀN TẤT (`POST :id/payment-method`). Chỉ đổi SANG Tiền mặt / Công nợ —
 * sang Chuyển khoản phải gán giao dịch ngân hàng. BE chặn khi tiền đã thu theo hình thức cũ (tiền mặt đã nộp,
 * công nợ đã trả, chuyển khoản đã nhận) và tự huỷ / ghi khoản thu trong sổ quỹ tài xế.
 */
const ChangePaymentMethodModal: React.FC<Props> = ({ orderId, orderCode, paymentMethod, onClose, onSaved }) => {
  const { request: post } = useFetchData.Post<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const options = [PM_CASH, PM_DEBT].filter((pm) => pm !== Number(paymentMethod));
  const [target, setTarget] = useState<number>(options[0]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!reason.trim()) return;
    setSaving(true);
    try {
      const res = await post({
        endPoint: API_ORDER_ROUTER.API_CHANGE_PAYMENT_METHOD(orderId),
        data: { payment_method: target, reason: reason.trim() },
      });
      if (!res || !isSuccess(res)) {
        showToast({ message: res?.message || "Đổi hình thức thanh toán thất bại", type: "error" });
        return;
      }
      // Đơn đã đổi nhưng sổ quỹ tài xế chưa cập nhật được → phải báo kế toán.
      const cashLedger = (res as any)?.data?.cash_ledger;
      if (cashLedger && cashLedger.synced === false) {
        showToast({ message: cashLedger.message || "Đã đổi nhưng chưa cập nhật được sổ quỹ tài xế", type: "warning" });
      } else {
        showToast({ message: `Đã đổi sang ${LABELS[target]}`, type: "success" });
      }
      onSaved();
    } catch (e: any) {
      showToast({ message: e?.message || "Đổi hình thức thanh toán thất bại", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[480px] max-w-[95vw] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-slate-800">Đổi hình thức thanh toán</h3>
          <p className="text-xs text-slate-500 mt-1">
            Đơn {orderCode ? <b>{orderCode}</b> : `#${orderId}`} — hiện tại:{" "}
            <b>{LABELS[Number(paymentMethod)] || "—"}</b>
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">Đổi sang</label>
            <div className="flex gap-3">
              {options.map((pm) => (
                <label
                  key={pm}
                  className={`flex-1 cursor-pointer rounded-lg border px-3 py-2 text-sm text-center transition ${
                    target === pm ? "border-teal-500 bg-teal-50 text-teal-700 font-medium" : "border-slate-200 text-slate-600"
                  }`}
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={target === pm}
                    onChange={() => setTarget(pm)}
                  />
                  {LABELS[pm]}
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            {target === PM_CASH
              ? "Khoản thu tiền mặt sẽ được ghi vào sổ quỹ của nhân viên đã hoàn thành đơn (tài xế giao / NV kiểm đếm giao)."
              : "Đơn sẽ được ghi công nợ cho khách theo tổng tiền đơn."}
            {Number(paymentMethod) === PM_CASH && " Khoản thu tiền mặt cũ trong sổ quỹ tài xế sẽ bị huỷ (chỉ khi tài xế chưa nộp)."}
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Lý do <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-300 focus:outline-none resize-none"
              rows={2}
              maxLength={255}
              placeholder="VD: Khách xin ghi nợ, tài xế bấm nhầm tiền mặt..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition"
          >
            Trở lại
          </button>
          <button
            onClick={save}
            disabled={saving || !reason.trim() || !target}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition disabled:opacity-50"
          >
            {saving ? "Đang lưu..." : "Xác nhận đổi"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePaymentMethodModal;
